import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import type {
  AIFeature,
  AIFeatureConfig,
  AIFeatureConfigUpdate,
  AIFeatureConfigUpdateWithId,
  AIMetadataItem,
  AIProviderConfig,
  AiResponseBackgroundJobDataDto,
  AiResponseBackgroundJobIdDto,
  GetAIMetadataApiResponse,
  GetAllFeatureConfigsApiRequest,
  GetAllFeatureConfigsApiResponse,
  GetAllLlmModelRegistryEntriesApiResponse,
  GetAllProviderConfigsApiResponse,
  GetFeatureConfigApiRequest,
  GetFeatureConfigApiResponse,
  GetFeatureListApiResponse,
  GetGenerateRecordFieldValuesJobDetailsApiResult,
  GetImportFormJobApiRequest,
  GetImportFormJobApiResponse,
  GetImportFormJobApiResult,
  GetProviderModelsApiRequest,
  GetProviderModelsApiResponse,
  GetProvidersApiRequest,
  GetProvidersApiResponse,
  GetRecordAiAnalysisPromptApiArg,
  GetRecordAiAnalysisPromptApiResponse,
  LlmModelRegisterDto,
  PatchLlmModelRegistryEntryApiArg,
  PatchLlmModelRegistryEntryApiResponse,
  PostAIMetadataApiRequest,
  PostAIMetadataApiResponse,
  PostGenerateFieldsValuesJobApiRequest,
  PostGenerateFieldsValuesJobApiResult,
  PostImportFormJobApiRequest,
  PostImportFormJobApiResponse,
  PostImportFormJobApiResult,
  PostProviderConfigApiRequest,
  PostProviderConfigApiResponse,
  PutAIMetadataApiRequest,
  PutAIMetadataApiResponse,
  PutProviderConfigApiRequest,
  PutProviderConfigApiResponse,
  SaveFeatureConfigApiRequest,
  SaveFeatureConfigApiResponse,
  UpdateFeatureConfigApiRequest,
  UpdateFeatureConfigApiResponse,
  UpdateRecordAiAnalysisPromptApiArg,
  UpdateRecordAiAnalysisPromptApiResponse,
} from '../../@types/ai-api';
import type { Caption } from '../../@types/common';
import { addIdToGeneratedForm } from '../../utils/form-builder-utils';
import { api } from './api';
import { providesList } from '../util/api-util';

export const aiApi = api
  .enhanceEndpoints({
    addTagTypes: [
      'ProviderConfig',
      'AIMetadata',
      'FeatureConfig',
      'FormLevelAIAnalysisPrompt',
      'SingleRecordAIAnalysisPrompt',
      'AllLlmModelRegistryEntries',
    ],
  })
  .injectEndpoints({
    overrideExisting: 'throw',
    endpoints: (builder) => ({
      postImportFormJob: builder.mutation<PostImportFormJobApiResult, PostImportFormJobApiRequest>({
        query: ({ formData }) => ({
          url: '/ai/forms/convert/start',
          method: 'POST',
          body: formData,
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        transformResponse: (response: PostImportFormJobApiResponse) => response.result,
      }),
      postImportFormFromTextJob: builder.mutation<PostImportFormJobApiResult, PostImportFormJobApiRequest>({
        query: ({ formData }) => ({
          url: `/ai/forms/generate/start`,
          method: 'POST',
          body: formData,
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        transformResponse: (response: PostImportFormJobApiResponse) => response.result,
      }),
      getImportFormJob: builder.query<GetImportFormJobApiResult, GetImportFormJobApiRequest>({
        query: (jobId) => ({
          url: `/ai/forms/${jobId}`,
          method: 'GET',
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        transformResponse: (response: GetImportFormJobApiResponse) => {
          return {
            ...response.result,
            result: {
              ...response.result!.result,
              formCaption: response.result!.result?.formCaption as Caption,
              fieldGroups: addIdToGeneratedForm(response.result!.result?.fieldGroups ?? []),
            },
          };
        },
      }),
      getProviders: builder.query<Array<string>, GetProvidersApiRequest>({
        query: ({ hasModel }) => ({
          url: `/ai/provider-model/providers?hasModel=${hasModel}`,
          method: 'GET',
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        transformResponse: (response: GetProvidersApiResponse) => response.result ?? [],
      }),
      getProviderModels: builder.query<Array<string>, GetProviderModelsApiRequest>({
        query: ({ provider }) => ({
          url: `/ai/provider-model/models?provider=${provider}`,
          method: 'GET',
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        transformResponse: (response: GetProviderModelsApiResponse) => response.result ?? [],
      }),
      getAllProviderConfigs: builder.query<Array<AIProviderConfig>, void>({
        query: () => ({
          url: '/ai/provider-config/all',
          method: 'GET',
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        transformResponse: (response: GetAllProviderConfigsApiResponse) => response.result ?? [],
        providesTags: (result) =>
          result
            ? [
                ...result.map((item) => ({ type: 'ProviderConfig' as const, id: item.id })),
                { type: 'ProviderConfig', id: 'LIST' },
              ]
            : [{ type: 'ProviderConfig', id: 'LIST' }],
      }),
      saveProviderConfig: builder.mutation<string, PostProviderConfigApiRequest>({
        query: (body) => ({
          url: '/ai/provider-config',
          method: 'POST',
          body: body,
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        transformResponse: (response: PostProviderConfigApiResponse) => response.result,
        invalidatesTags: [{ type: 'ProviderConfig', id: 'LIST' }],
      }),
      updateProviderConfig: builder.mutation<string, PutProviderConfigApiRequest>({
        query: ({ id, ...body }) => ({
          url: `/ai/provider-config/${id}`,
          method: 'PUT',
          body: body,
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        transformResponse: (response: PutProviderConfigApiResponse) => response.result,
        invalidatesTags: [{ type: 'ProviderConfig', id: 'LIST' }],
      }),
      getAIMetadata: builder.query<Array<AIMetadataItem>, void>({
        query: () => ({
          url: '/metadata/ai-config',
          method: 'GET',
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        transformResponse: (response: GetAIMetadataApiResponse) => response.result ?? [],
        providesTags: (result) =>
          result
            ? [
                ...result.map((item) => ({ type: 'AIMetadata' as const, id: item.id })),
                { type: 'AIMetadata', id: 'LIST' },
              ]
            : [{ type: 'AIMetadata', id: 'LIST' }],
      }),
      updateAIMetadata: builder.mutation<boolean, PutAIMetadataApiRequest>({
        query: (body) => ({
          url: '/metadata/ai-config',
          method: 'PUT',
          body: body,
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        transformResponse: (response: PutAIMetadataApiResponse) => response.result,
        invalidatesTags: [{ type: 'AIMetadata', id: 'LIST' }],
      }),
      bulkUpdateAIMetadata: builder.mutation<boolean, PostAIMetadataApiRequest>({
        query: (body) => ({
          url: '/metadata/ai-config',
          method: 'POST',
          body: body,
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        transformResponse: (response: PostAIMetadataApiResponse) => response.result,
        invalidatesTags: [{ type: 'AIMetadata', id: 'LIST' }],
      }),
      postGenerateFieldsValuesJob: builder.mutation<
        AiResponseBackgroundJobIdDto,
        PostGenerateFieldsValuesJobApiRequest
      >({
        query: ({ aiRecordFieldsGenerationRequestDto }) => ({
          url: `/ai/record-fields/generate/start`,
          method: 'POST',
          body: aiRecordFieldsGenerationRequestDto,
          validateStatus: (response, result) => response.status === 200 && result,
        }),
        transformResponse: (response: PostGenerateFieldsValuesJobApiResult) => response.result!,
      }),
      getGenerateRecordFieldValuesJobDetails: builder.query<AiResponseBackgroundJobDataDto, string>({
        query: (jobId) => ({
          url: `/ai/record-fields/${jobId}`,
          method: 'GET',
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        transformResponse: (response: GetGenerateRecordFieldValuesJobDetailsApiResult) => response.result!,
      }),
      getFeatureList: builder.query<Array<AIFeature>, void>({
        query: () => ({
          url: '/ai/feature-config/feature-list',
          method: 'GET',
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        transformResponse: (response: GetFeatureListApiResponse) => response.result ?? [],
      }),
      getAllFeatureConfigs: builder.query<Array<AIFeatureConfig>, GetAllFeatureConfigsApiRequest>({
        query: () => ({
          url: '/ai/feature-config/all',
          method: 'GET',
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        transformResponse: (response: GetAllFeatureConfigsApiResponse) => response.result ?? [],
        providesTags: (result) =>
          result
            ? [
                ...result.map((item) => ({ type: 'FeatureConfig' as const, id: item.id })),
                { type: 'FeatureConfig', id: 'LIST' },
              ]
            : [{ type: 'FeatureConfig', id: 'LIST' }],
      }),
      getFeatureConfig: builder.query<AIFeatureConfig, GetFeatureConfigApiRequest>({
        query: (queryArg) => ({
          url: `/ai/feature-config/${queryArg.configId}`,
          method: 'GET',
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        transformResponse: (response: GetFeatureConfigApiResponse) => response.result!,
      }),
      saveFeatureConfig: builder.mutation<string, SaveFeatureConfigApiRequest>({
        query: ({ aiFeatureConfig }) => ({
          url: '/ai/feature-config',
          method: 'POST',
          body: aiFeatureConfig,
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        transformResponse: (response: SaveFeatureConfigApiResponse) => response.result!,
        invalidatesTags: [{ type: 'FeatureConfig', id: 'LIST' }],
      }),
      updateFeatureConfig: builder.mutation<string, UpdateFeatureConfigApiRequest>({
        query: ({ configId, aiFeatureConfig }) => ({
          url: `/ai/feature-config/${configId}`,
          method: 'PATCH',
          body: aiFeatureConfig,
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        transformResponse: (response: UpdateFeatureConfigApiResponse) => response.result!,
        invalidatesTags: [{ type: 'FeatureConfig', id: 'LIST' }],
      }),
      getBulkRecordAiAnalysisPrompt: builder.query<
        GetRecordAiAnalysisPromptApiResponse,
        GetRecordAiAnalysisPromptApiArg
      >({
        query: (queryArg) => ({ url: `/forms/${queryArg.formId}/bulk-record-ai-analysis-prompt` }),
        providesTags: ['FormLevelAIAnalysisPrompt'],
      }),
      updateBulkRecordAiAnalysisPrompt: builder.mutation<
        UpdateRecordAiAnalysisPromptApiResponse,
        UpdateRecordAiAnalysisPromptApiArg
      >({
        query: (queryArg) => ({
          url: `/forms/${queryArg.formId}/bulk-record-ai-analysis-prompt`,
          method: 'PUT',
          body: queryArg.body,
        }),
        invalidatesTags: ['FormLevelAIAnalysisPrompt'],
      }),
      getSingleRecordAiAnalysisPrompt: builder.query<
        GetRecordAiAnalysisPromptApiResponse,
        GetRecordAiAnalysisPromptApiArg
      >({
        query: (queryArg) => ({ url: `/forms/${queryArg.formId}/single-record-ai-analysis-prompt` }),
        providesTags: ['SingleRecordAIAnalysisPrompt'],
      }),
      updateSingleRecordAiAnalysisPrompt: builder.mutation<
        UpdateRecordAiAnalysisPromptApiResponse,
        UpdateRecordAiAnalysisPromptApiArg
      >({
        query: (queryArg) => ({
          url: `/forms/${queryArg.formId}/single-record-ai-analysis-prompt`,
          method: 'PUT',
          body: queryArg.body,
        }),
        invalidatesTags: ['SingleRecordAIAnalysisPrompt'],
      }),
      getAllLlmModelRegistryEntries: builder.query<LlmModelRegisterDto[], void>({
        query: () => ({
          url: `/ai/llm-model-registry/all`,
          validateStatus: (response, result) => response.status === 200 && result?.result,
        }),
        providesTags: (result) => providesList(result, 'AllLlmModelRegistryEntries'),
        transformResponse: (response: GetAllLlmModelRegistryEntriesApiResponse) => response.result!,
      }),
      patchLlmModelRegistryEntry: builder.mutation<
        PatchLlmModelRegistryEntryApiResponse,
        PatchLlmModelRegistryEntryApiArg
      >({
        query: (queryArg) => ({
          url: `/ai/llm-model-registry/${queryArg.id}`,
          method: 'PATCH',
          body: queryArg.llmModelRegisterDto,
        }),
        invalidatesTags: (result, error, arg) => {
          return [{ type: 'AllLlmModelRegistryEntries', id: arg.id }];
        },
      }),
    }),
  });

const customisedAiApi = aiApi.injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => ({
    bulkAddFeatureConfigs: builder.mutation<boolean, Array<AIFeatureConfigUpdate>>({
      async queryFn(arg, api, extraOptions, fetchBaseQuery) {
        const promiseMutations = [];

        for (const item of arg) {
          promiseMutations.push(fetchBaseQuery({ url: '/ai/feature-config', method: 'POST', body: item }));
        }

        try {
          const results = await Promise.allSettled(promiseMutations);

          const errors = results.filter(
            (result) => result.status === 'rejected' || (result.status === 'fulfilled' && result.value.error)
          );

          if (errors.length > 0) {
            return {
              error: {
                data: 'Unable to add or update feature configurations',
                status: 500,
              } as FetchBaseQueryError,
            };
          }

          return { data: true };
        } catch (_) {
          return {
            error: {
              data: 'Unable to add or update feature configurations',
              status: 500,
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: [{ type: 'FeatureConfig', id: 'LIST' }],
    }),
    bulkUpdateFeatureConfigs: builder.mutation<boolean, Array<AIFeatureConfigUpdateWithId>>({
      async queryFn(arg, api, extraOptions, fetchBaseQuery) {
        const promiseMutations = [];

        for (const item of arg) {
          promiseMutations.push(fetchBaseQuery({ url: `/ai/feature-config/${item.id}`, method: 'PATCH', body: item }));
        }

        try {
          await Promise.allSettled(promiseMutations);
          return { data: true };
        } catch (_) {
          return {
            error: {
              data: 'Unable to update feature configurations',
              status: 500,
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: [{ type: 'FeatureConfig', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetImportFormJobQuery,
  usePostImportFormJobMutation,
  usePostImportFormFromTextJobMutation,
  useGetProvidersQuery,
  useGetProviderModelsQuery,
  useGetAllProviderConfigsQuery,
  useSaveProviderConfigMutation,
  useUpdateProviderConfigMutation,
  useGetAIMetadataQuery,
  useUpdateAIMetadataMutation,
  usePostGenerateFieldsValuesJobMutation,
  useGetGenerateRecordFieldValuesJobDetailsQuery,
  useGetFeatureListQuery,
  useGetAllFeatureConfigsQuery,
  useGetFeatureConfigQuery,
  useSaveFeatureConfigMutation,
  useUpdateFeatureConfigMutation,
  useBulkUpdateAIMetadataMutation,
  useBulkAddFeatureConfigsMutation,
  useBulkUpdateFeatureConfigsMutation,
  useGetBulkRecordAiAnalysisPromptQuery,
  useUpdateBulkRecordAiAnalysisPromptMutation,
  useGetSingleRecordAiAnalysisPromptQuery,
  useUpdateSingleRecordAiAnalysisPromptMutation,
  usePatchLlmModelRegistryEntryMutation,
  useGetAllLlmModelRegistryEntriesQuery,
} = customisedAiApi;
